import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Clock, LogIn, LogOut, Anchor, Sparkles, Megaphone,
  CalendarDays, Ship, Waves, Maximize, Minimize, Heart,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cbt:tv_token";
const REFRESH_MS = 30_000;
const CAROUSEL_MS = 8_000;
const PHOTOS_REFRESH_MS = 60_000;

const WEEKDAY_NO = [
  "Mandag", "Tirsdag", "Onsdag", "Torsdag",
  "Fredag", "Lørdag", "Søndag",
];
const MONTH_NO = [
  "januar", "februar", "mars", "april", "mai", "juni",
  "juli", "august", "september", "oktober", "november", "desember",
];

const SEASON_LABEL = {
  active: "Aktiv sesong",
  winter_prep: "Vinterklargjøring",
  winter_storage: "Vinteropplag",
  spring_prep: "Vårklargjøring",
};

const STATIC_CAROUSEL = [
  { src: "/boat-39-side.jpg", caption: "Canal Boats Telemark – stille kraft på Telemarkskanalen" },
  { src: "/boat-39-rear.jpg", caption: "39-fots e-yacht med panoramautsikt" },
  { src: "/boat-33-side.jpg", caption: "33-fots e-yacht – perfekt for par & små grupper" },
  { src: "/boat-33-rear.jpg", caption: "Elektrisk drift – stillegående og bærekraftig" },
];

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
};

const fmtDateLong = (d) => {
  const wd = WEEKDAY_NO[(d.getDay() + 6) % 7]; // JS: Sun=0 → shift to Mon=0
  return `${wd} ${d.getDate()}. ${MONTH_NO[d.getMonth()]} ${d.getFullYear()}`;
};

function TokenGate({ onToken }) {
  const [val, setVal] = useState("");
  return (
    <div
      data-testid="tv-token-gate"
      className="min-h-screen flex items-center justify-center bg-[#0F1A2B] text-white p-8"
    >
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="flex justify-center">
          <div className="h-16 w-16 rounded-full bg-[#A57F39]/20 flex items-center justify-center">
            <Ship className="h-8 w-8 text-[#BD984D]" />
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.4em] text-[#BD984D]">
            Canal Boats Telemark
          </div>
          <h1 className="text-3xl font-semibold mt-2">Kontorskjerm</h1>
          <p className="text-sm text-slate-300 mt-3">
            Denne skjermen er ikke autorisert enda. Lim inn TV-token fra
            <span className="text-[#BD984D]"> Innstillinger → Kontorskjerm </span>
            for å aktivere.
          </p>
        </div>
        <input
          data-testid="tv-token-input"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="TV-token"
          className="w-full rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#BD984D]"
        />
        <button
          data-testid="tv-token-submit"
          disabled={!val.trim()}
          onClick={() => onToken(val.trim())}
          className="w-full rounded-lg bg-[#A57F39] hover:bg-[#BD984D] transition-colors py-3 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Aktiver skjerm
        </button>
        <p className="text-xs text-slate-400">
          Skjermen krever ingen innlogging etter aktivering – tokenet lagres
          lokalt i nettleseren og utløper aldri.
        </p>
      </div>
    </div>
  );
}

function Header({ now, seasonMode }) {
  return (
    <div
      data-testid="tv-header"
      className="flex items-center justify-between px-8 py-3 border-b border-white/10 bg-gradient-to-r from-[#0F1A2B] via-[#152238] to-[#0F1A2B] flex-shrink-0"
    >
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-full bg-[#A57F39]/20 border border-[#BD984D]/30 flex items-center justify-center">
          <Anchor className="h-5 w-5 text-[#BD984D]" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.42em] text-[#BD984D]">
            Canal Boats Telemark
          </div>
          <h1 className="text-2xl font-bold text-white leading-tight">
            Velkommen til Canal Boats
          </h1>
        </div>
      </div>
      <div className="text-right">
        <div
          data-testid="tv-clock"
          className="text-4xl font-mono font-bold text-white tabular-nums leading-none"
        >
          {now.toLocaleTimeString("nb-NO", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
        <div className="text-xs text-slate-300 mt-1 capitalize">
          {fmtDateLong(now)}
        </div>
        <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-[#BD984D]">
          <Waves className="h-3 w-3" />
          {SEASON_LABEL[seasonMode] || seasonMode}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, count, children, tone = "gold", testid }) {
  const toneMap = {
    gold: "border-[#BD984D]/40 shadow-[0_0_40px_-20px_rgba(189,152,77,0.6)]",
    teal: "border-cyan-500/40 shadow-[0_0_40px_-20px_rgba(6,182,212,0.5)]",
    slate: "border-white/10",
  };
  const badgeTone = {
    gold: "bg-[#A57F39] text-white",
    teal: "bg-cyan-600 text-white",
    slate: "bg-white/10 text-white",
  };
  return (
    <div
      data-testid={testid}
      className={`h-full rounded-xl bg-[#152238]/80 backdrop-blur border ${toneMap[tone]} p-4 flex flex-col min-h-0`}
    >
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-[#BD984D]" />
          <h2 className="text-base font-semibold text-white uppercase tracking-wider">
            {title}
          </h2>
        </div>
        {count != null && (
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${badgeTone[tone]}`}
          >
            {count}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-slate-400 text-sm italic h-full flex items-center justify-center">
      {text}
    </div>
  );
}

function BookingRow({ b, kind }) {
  const time = kind === "checkin"
    ? fmtTime(b.start_date) || "14:00"
    : fmtTime(b.end_date) || "10:00";
  return (
    <div className="flex items-center justify-between py-3 border-b border-white/5 last:border-b-0">
      <div className="min-w-0">
        <div className="text-white font-semibold text-lg truncate">
          {b.boat_name || "—"}
        </div>
        <div className="text-slate-300 text-sm truncate">
          {b.customer_name || "Ukjent kunde"}
          {b.num_guests ? ` · ${b.num_guests} gjester` : ""}
        </div>
      </div>
      <div className="text-right shrink-0 ml-3">
        <div className="text-2xl font-mono font-bold text-[#BD984D] tabular-nums">
          {time}
        </div>
      </div>
    </div>
  );
}

function BoatChip({ name, pct }) {
  const pctColor =
    pct == null ? "text-slate-400"
    : pct < 30 ? "text-red-400"
    : pct < 60 ? "text-amber-400"
    : "text-emerald-400";
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-b-0">
      <div className="flex items-center gap-3">
        <Ship className="h-4 w-4 text-[#BD984D]" />
        <span className="text-white text-lg font-medium">{name || "—"}</span>
      </div>
      {pct != null && (
        <span className={`text-sm font-mono ${pctColor}`}>{pct}%</span>
      )}
    </div>
  );
}

function Carousel({ token }) {
  const [i, setI] = useState(0);
  const [customerPhotos, setCustomerPhotos] = useState([]);

  // Refresh approved customer photos periodically so freshly approved
  // pictures appear on the TV without a full page reload.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/tv/customer-photos`, {
          headers: { "X-TV-Token": token },
        });
        if (alive) setCustomerPhotos(Array.isArray(r.data) ? r.data : []);
      } catch { /* keep last-known list on transient errors */ }
    };
    load();
    const id = setInterval(load, PHOTOS_REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, [token]);

  // Build the merged slide list: alternate static fleet imagery with
  // approved customer photos so both feel present without either
  // dominating the screen.
  const slides = useMemo(() => {
    if (customerPhotos.length === 0) return STATIC_CAROUSEL;
    const merged = [];
    const maxLen = Math.max(STATIC_CAROUSEL.length, customerPhotos.length);
    for (let n = 0; n < maxLen; n += 1) {
      if (STATIC_CAROUSEL[n]) merged.push({ ...STATIC_CAROUSEL[n], kind: "fleet" });
      if (customerPhotos[n]) {
        const cp = customerPhotos[n];
        const src = cp.image_url && cp.image_url.startsWith("http")
          ? cp.image_url
          : `${BACKEND_URL}${cp.image_url}`;
        // Drive-uploaded photos (source="drive") have no attribution,
        // so keep them clean: no caption, no badge. Customer-approved
        // photos keep their friendly "Fra <navn>" tag.
        const isDrive = cp.source === "drive";
        merged.push({
          src,
          caption: isDrive
            ? ""
            : (cp.caption ||
                (cp.user_first_name
                  ? `Delt av ${cp.user_first_name}${cp.boat_name ? ` · ${cp.boat_name}` : ""}`
                  : "Delt av en av våre gjester")),
          kind: "customer",
          badge: isDrive
            ? null
            : (cp.user_first_name ? `Fra ${cp.user_first_name}` : "Fra gjest"),
        });
      }
    }
    return merged;
  }, [customerPhotos]);

  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % slides.length), CAROUSEL_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  // Keep index in bounds when the slide list shrinks or grows.
  useEffect(() => {
    if (i >= slides.length) setI(0);
  }, [slides.length, i]);

  const item = slides[i] || slides[0];
  return (
    <div
      data-testid="tv-carousel"
      className="relative rounded-2xl overflow-hidden border border-[#BD984D]/30 h-full min-h-[240px] bg-[#0F1A2B]"
    >
      {slides.map((it, idx) => (
        <img
          key={`${it.src}-${idx}`}
          src={it.src}
          alt=""
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            idx === i ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A2B] via-[#0F1A2B]/40 to-transparent" />
      {item?.kind === "customer" && (
        <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-[#BD984D] text-[#0C1824] text-[10px] uppercase font-bold tracking-widest px-3 py-1 shadow-lg">
          <Heart className="h-3 w-3" />
          {item.badge || "Fra en gjest"}
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <div className="text-xs uppercase tracking-[0.32em] text-[#BD984D]">
          {item?.kind === "customer" ? "Gjestebilder" : "Vår flåte"}
        </div>
        <div className="text-white text-2xl font-semibold mt-1 drop-shadow">
          {item?.caption}
        </div>
      </div>
    </div>
  );
}

function CustomItems({ items, recurring }) {
  const all = [
    ...recurring.map((r) => ({
      id: r.id,
      title: r.title + (r.time ? ` – kl. ${r.time}` : ""),
      body: r.body,
      kind: "task",
      is_today: r.is_today,
    })),
    ...items.map((x) => ({
      id: x.id,
      title: x.title,
      body: x.body,
      kind: x.kind,
      is_today: false,
    })),
  ];
  if (all.length === 0) return <EmptyState text="Ingen oppslag i dag." />;
  const iconFor = (kind) =>
    kind === "campaign" ? Sparkles : kind === "task" ? CalendarDays : Megaphone;
  return (
    <div className="space-y-3 overflow-y-auto pr-1 h-full">
      {all.map((it) => {
        const Icon = iconFor(it.kind);
        const highlight = it.is_today || it.kind === "campaign";
        return (
          <div
            key={it.id}
            className={`rounded-xl p-4 border ${
              highlight
                ? "bg-[#A57F39]/15 border-[#BD984D]/50"
                : "bg-white/5 border-white/10"
            }`}
          >
            <div className="flex items-start gap-3">
              <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${
                highlight ? "text-[#BD984D]" : "text-cyan-400"
              }`} />
              <div className="min-w-0">
                <div className="text-white font-semibold text-lg leading-tight">
                  {it.title}
                  {it.is_today && (
                    <span className="ml-2 text-xs uppercase tracking-widest bg-[#BD984D] text-white px-2 py-0.5 rounded">
                      I dag
                    </span>
                  )}
                </div>
                {it.body && (
                  <div className="text-slate-200 text-sm mt-1 leading-relaxed">
                    {it.body}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FullscreenButton({ inline = false }) {
  const [isFullscreen, setIsFullscreen] = useState(
    typeof document !== "undefined" && !!document.fullscreenElement,
  );
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      document.documentElement.requestFullscreen?.();
    }
  };
  const Icon = isFullscreen ? Minimize : Maximize;
  const label = isFullscreen ? "Avslutt fullskjerm (F)" : "Fullskjerm (F)";
  if (inline) {
    return (
      <button
        onClick={toggle}
        data-testid="tv-fullscreen-toggle"
        title={label}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 px-3 py-1.5 text-slate-300 hover:text-white transition"
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="uppercase tracking-widest">
          {isFullscreen ? "Avslutt fullskjerm" : "Fullskjerm"}
        </span>
      </button>
    );
  }
  return (
    <button
      onClick={toggle}
      data-testid="tv-fullscreen-toggle"
      title={label}
      className="fixed bottom-4 right-4 z-50 h-11 w-11 rounded-full bg-black/40 hover:bg-black/60 border border-white/20 text-white flex items-center justify-center backdrop-blur transition opacity-40 hover:opacity-100 focus:opacity-100"
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}

function WelcomeStrip({ names }) {
  if (!names || names.length === 0) return null;
  // Rens ut duplikater og tomme.
  const unique = Array.from(new Set(names.map((n) => (n || "").trim()).filter(Boolean)));
  if (unique.length === 0) return null;
  const listNo = (arr) => {
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]} & ${arr[1]}`;
    return `${arr.slice(0, -1).join(", ")} & ${arr[arr.length - 1]}`;
  };
  return (
    <div
      data-testid="tv-welcome-strip"
      className="mx-4 mt-3 rounded-xl border border-[#BD984D]/50 bg-gradient-to-r from-[#A57F39]/25 via-[#BD984D]/20 to-[#0C6E7E]/20 backdrop-blur px-5 py-2.5 flex items-center gap-3 shadow-[0_0_60px_-20px_rgba(189,152,77,0.55)] flex-shrink-0"
    >
      <div className="h-9 w-9 rounded-full bg-[#BD984D]/25 border border-[#BD984D]/50 flex items-center justify-center shrink-0">
        <Heart className="h-4 w-4 text-[#ECC974]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-[0.42em] text-[#ECC974] leading-none">
          Hjertelig velkommen
        </div>
        <div className="text-white text-xl font-bold leading-tight truncate mt-0.5">
          Velkommen, {listNo(unique)}!
        </div>
      </div>
      <div className="hidden xl:block text-slate-200 text-xs italic max-w-xs text-right">
        Ha en fantastisk tur på Telemarkskanalen
      </div>
    </div>
  );
}

export default function OfficeTvDashboard() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(() => {
    const url = params.get("token");
    if (url) {
      try { localStorage.setItem(TOKEN_KEY, url); } catch { /* noop */ }
      return url;
    }
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(new Date());
  const failCountRef = useRef(0);

  // Live clock (once per second, cheap)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Keyboard shortcut: "F" toggles fullscreen (except when typing in input).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "f" && e.key !== "F") return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      e.preventDefault();
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        document.documentElement.requestFullscreen?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Poll snapshot
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/tv/snapshot`, {
          headers: { "X-TV-Token": token },
        });
        if (cancelled) return;
        setData(r.data);
        setError("");
        failCountRef.current = 0;
      } catch (e) {
        if (cancelled) return;
        failCountRef.current += 1;
        if (e?.response?.status === 401) {
          setError("Ugyldig TV-token. Kontakt admin for å hente et nytt.");
          try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
          setToken("");
        } else {
          setError("Kunne ikke oppdatere data (prøver igjen…).");
        }
      }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  const acceptToken = (t) => {
    try { localStorage.setItem(TOKEN_KEY, t); } catch { /* noop */ }
    setToken(t);
    setError("");
  };

  if (!token) {
    return <TokenGate onToken={acceptToken} />;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F1A2B] text-white flex items-center justify-center">
        <div className="text-[#BD984D] uppercase tracking-[0.32em] text-sm">
          Laster kontorskjerm…
          {error && (
            <div className="text-red-400 mt-4 normal-case tracking-normal text-xs">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  const {
    departures = [], arrivals = [], on_tour = [],
    needs_wash = [], custom_items = [], recurring_tasks = [],
    season_mode = "active", welcome_names = [],
  } = data;

  // Build only the operational cards that actually have content. Empty
  // rubrikker skjules helt slik at aktive oppslag får all plass og
  // oppmerksomhet på skjermen.
  const activeOps = [
    departures.length > 0 && {
      key: "dep",
      testid: "tv-departures",
      title: "Avganger i dag",
      icon: LogOut,
      tone: "gold",
      count: departures.length,
      render: () => (
        <div className="overflow-y-auto h-full pr-1">
          {departures.map((b) => (
            <BookingRow key={b.booking_id + "-out"} b={b} kind="checkin" />
          ))}
        </div>
      ),
    },
    arrivals.length > 0 && {
      key: "arr",
      testid: "tv-arrivals",
      title: "Ankomster i dag",
      icon: LogIn,
      tone: "teal",
      count: arrivals.length,
      render: () => (
        <div className="overflow-y-auto h-full pr-1">
          {arrivals.map((b) => (
            <BookingRow key={b.booking_id + "-in"} b={b} kind="checkout" />
          ))}
        </div>
      ),
    },
    on_tour.length > 0 && {
      key: "tour",
      testid: "tv-on-tour",
      title: "Ute på tur",
      icon: Ship,
      tone: "slate",
      count: on_tour.length,
      render: () => (
        <div className="overflow-y-auto h-full pr-1">
          {on_tour.map((b) => (
            <BoatChip key={b.boat_id} name={b.boat_name} pct={b.battery_pct} />
          ))}
        </div>
      ),
    },
    needs_wash.length > 0 && {
      key: "wash",
      testid: "tv-needs-wash",
      title: "Trenger vask",
      icon: Sparkles,
      tone: "teal",
      count: needs_wash.length,
      render: () => (
        <div className="overflow-y-auto h-full pr-1">
          {needs_wash.map((b) => (
            <BoatChip key={b.boat_id} name={b.boat_name} />
          ))}
        </div>
      ),
    },
  ].filter(Boolean);

  const announcementsCount = custom_items.length + recurring_tasks.length;
  const hasAnnouncements = announcementsCount > 0;
  const allEmpty = activeOps.length === 0 && !hasAnnouncements;

  // Auto-grid tettere når få rubrikker er aktive:
  //  1 aktiv → full bredde,  2 → halv/halv,  3 → tre kolonner,  4 → 2×2
  const opsGridCls =
    activeOps.length <= 1
      ? "grid-cols-1"
      : activeOps.length === 2
      ? "grid-cols-2"
      : activeOps.length === 3
      ? "grid-cols-3"
      : "grid-cols-2 grid-rows-2";

  return (
    <div
      data-testid="tv-dashboard-root"
      className="min-h-screen h-screen w-screen bg-[#0F1A2B] text-white flex flex-col overflow-hidden font-['Manrope']"
    >
      <Header now={now} seasonMode={season_mode} />
      <WelcomeStrip names={welcome_names} />

      {allEmpty ? (
        // Fullscreen carousel takeover when every operational tab is empty
        // so the TV still feels alive and on-brand rather than showing a
        // grid of "ingenting"-cards.
        <div
          className="flex-1 min-h-0 p-4"
          data-testid="tv-idle-carousel"
        >
          <div className="relative h-full">
            <Carousel token={token} />
            <div className="absolute top-6 left-6 z-10 bg-black/45 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/10">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
                Rolig dag på kaia
              </div>
              <div className="text-sm text-white/85 mt-0.5">
                Ingen avganger eller ankomster registrert
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div
        className="flex-1 min-h-0 grid grid-cols-12 gap-3 p-4"
        data-testid="tv-active-grid"
      >
        {/* Venstre: kun rubrikker med innhold. Autogrid gir én full,
            to halve, eller opptil 2×2 avhengig av hvor mye som er aktivt.
            Kompakt kolonne (4/12) slik at bildet får hovedplassen. */}
        {activeOps.length > 0 && (
          <div className="col-span-4 min-h-0">
            <div className={`grid ${opsGridCls} gap-3 h-full`}>
              {activeOps.map((s) => (
                <div key={s.key} className="min-h-0">
                  <SectionCard
                    testid={s.testid}
                    title={s.title}
                    icon={s.icon}
                    count={s.count}
                    tone={s.tone}
                  >
                    {s.render()}
                  </SectionCard>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Høyre: karusellen får storparten av skjermen (8/12, eller
            full bredde om det ikke er noen aktive rubrikker). "Oppslag
            & rutiner" er en smal underliggende strip når den finnes. */}
        <div
          className={`${
            activeOps.length === 0 ? "col-span-12" : "col-span-8"
          } min-h-0 flex flex-col gap-3`}
        >
          <div className={hasAnnouncements ? "flex-[3] min-h-0" : "flex-1 min-h-0"}>
            <Carousel token={token} />
          </div>
          {hasAnnouncements && (
            <div className="flex-1 min-h-0">
              <SectionCard
                testid="tv-announcements"
                title="Oppslag & rutiner"
                icon={Megaphone}
                count={announcementsCount}
                tone="gold"
              >
                <CustomItems items={custom_items} recurring={recurring_tasks} />
              </SectionCard>
            </div>
          )}
        </div>
      </div>
      )}

      <div className="px-6 py-2 border-t border-white/10 flex items-center justify-between text-[11px] text-slate-400 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Clock className="h-3 w-3" />
          Oppdateres automatisk hvert 30. sekund
        </div>
        <div className="flex items-center gap-3">
          {error ? (
            <span className="text-amber-400" data-testid="tv-error">{error}</span>
          ) : (
            <span data-testid="tv-last-sync">
              Sist synkronisert {now.toLocaleTimeString("nb-NO")}
            </span>
          )}
          <FullscreenButton inline />
        </div>
      </div>
    </div>
  );
}
